import { useOutletContext } from "react-router-dom";
import { useState } from "react";
import { Button, Modal, Form, Radio, InputNumber, Input, message, Tooltip } from "antd";
import { CalendarOutlined, ThunderboltOutlined, DollarOutlined, EyeOutlined, TeamOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { EmployeeOutletContext, EmployeeIncentiveHistoryEntry } from "../incentiveCalculation/types";
import "./styles.scss";

/* ─── Helpers ─────────────────────────────────────────────────────────── */

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const statusLabel = (status: string) =>
  status === 'partial' ? 'Partial Paid' : status === 'unpaid' ? 'Unpaid' : 'Paid';

/* ─── Component ───────────────────────────────────────────────────────── */

const EmployeeIncentiveHistory = () => {
  const { incentiveHistory, setIncentiveHistory } = useOutletContext<EmployeeOutletContext>();

  // ── Pay modal state ──
  const [payEntry, setPayEntry] = useState<EmployeeIncentiveHistoryEntry | null>(null);
  const [paymentMode, setPaymentMode] = useState<'partial' | 'full'>('full');
  const [paymentAmount, setPaymentAmount] = useState<number | null>(0);
  const [paymentNote, setPaymentNote] = useState('');

  const openPayModal = (entry: EmployeeIncentiveHistoryEntry) => {
    setPayEntry(entry);
    setPaymentMode('full');
    setPaymentAmount(Math.max(0, entry.dueAmount));
    setPaymentNote('');
  };

  const closePayModal = () => setPayEntry(null);

  const handlePaySubmit = () => {
    if (!payEntry) return;
    const due = Math.max(0, payEntry.dueAmount);
    const amount = paymentMode === 'full' ? due : Math.min(Number(paymentAmount) || 0, due);
    if (amount <= 0) { message.error('Enter a valid amount'); return; }

    setIncentiveHistory(prev => prev.map(e => {
      if (e.id !== payEntry.id) return e;
      const paidAmount = e.paidAmount + amount;
      const dueAmount  = Math.max(0, e.finalTotal - paidAmount);
      return {
        ...e,
        paidAmount,
        dueAmount,
        paymentStatus: dueAmount <= 0 ? 'paid' : 'partial',
        payments: [...e.payments, { amount, paymentType: paymentMode, note: paymentNote, paidAt: dayjs().toISOString() }],
      };
    }));
    closePayModal();
  };

  return (
    <>
      <div className="incentive-history">
        <div className="ih-head">
          <div>
            <h3 className="ih-title">Incentive History</h3>
            <p className="ih-sub">
              Past incentive payouts &amp; slab history for this employee
            </p>
          </div>
        </div>

        {incentiveHistory.length === 0 ? (
          <div className="ih-empty">Incentive history will appear here.</div>
        ) : (
          <div className="ih-cards">
            {incentiveHistory.map((entry) => {
              const skey = entry.paymentStatus;
              return (
                <div className={`ih-card ih-card-${skey}`} key={entry.id}>
                  <div className="ih-card-top">
                    <div className="ih-card-head">
                      <span className="ih-card-period">
                        <CalendarOutlined />
                        {dayjs(entry.fromDate).format('DD MMM YYYY')}
                        {' – '}
                        {dayjs(entry.toDate).format('DD MMM YYYY')}
                      </span>
                      <span className="ih-card-date">
                        Generated {dayjs(entry.generatedAt).format('DD MMM YYYY, hh:mm A')}
                      </span>
                    </div>
                    <div className="ih-card-top-right">
                      <span className={`ih-pay-status ih-pay-status-${skey}`}>
                        {statusLabel(entry.paymentStatus)}
                      </span>
                      {skey !== 'paid' && entry.dueAmount > 0 && (
                        <Button size="small" type="primary" className="ih-pay-btn" onClick={() => openPayModal(entry)}>
                          Pay
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="ih-card-stats">
                    <div className="ih-stat">
                      <span className="ih-stat-icon"><ThunderboltOutlined /></span>
                      <div>
                        <div className="ih-stat-label">Sales</div>
                        <div className="ih-stat-value">{entry.noOfSales}</div>
                      </div>
                    </div>
                    <div className="ih-stat">
                      <span className="ih-stat-icon"><DollarOutlined /></span>
                      <div>
                        <div className="ih-stat-label">Revenue</div>
                        <div className="ih-stat-value">{inr(entry.revenueGenerated)}</div>
                      </div>
                    </div>
                    <div className="ih-stat">
                      <span className="ih-stat-icon">%</span>
                      <div>
                        <div className="ih-stat-label">Slab</div>
                        <div className="ih-stat-value">{entry.slabPercentage}%</div>
                      </div>
                    </div>
                    <div className="ih-stat ih-stat-highlight">
                      <span className="ih-stat-icon"><TeamOutlined /></span>
                      <div>
                        <div className="ih-stat-label">Total Clients</div>
                        <div className="ih-stat-value">{entry.totalClients}</div>
                      </div>
                    </div>
                  </div>

                  <div className="ih-card-breakup">
                    <div className="ih-row">
                      <span>Incentive Amount</span>
                      <span>{inr(entry.incentiveAmount)}</span>
                    </div>
                    <div className="ih-row">
                      <span>TDS ({entry.tdsPercent}%)</span>
                      <span className="ih-neg">− {inr(entry.tdsAmount)}</span>
                    </div>
                    <div className="ih-row">
                      <span>GST ({entry.gstPercent}%)</span>
                      <span className="ih-neg">− {inr(entry.gstAmount)}</span>
                    </div>
                    <div className="ih-row ih-total">
                      <span>Total Payable</span>
                      <span>{inr(entry.totalAmount)}</span>
                    </div>

                    {entry.additionalFields.length > 0 && (
                      <div className="ih-additional-fields">
                        {entry.additionalFields.map((f, idx) => (
                          <div className="ih-row" key={idx}>
                            <span>
                              {f.fieldName}
                              {f.remark && (
                                <Tooltip title={f.remark}>
                                  <EyeOutlined className="ih-remark-icon" />
                                </Tooltip>
                              )}
                            </span>
                            <span>{inr(f.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {entry.deduction > 0 && (
                      <div className="ih-row">
                        <span>
                          Deduction
                          {entry.deductionRemark && (
                            <Tooltip title={entry.deductionRemark}>
                              <EyeOutlined className="ih-remark-icon" />
                            </Tooltip>
                          )}
                        </span>
                        <span className="ih-neg">− {inr(entry.deduction)}</span>
                      </div>
                    )}

                    <div className="ih-row ih-total">
                      <span>Net Payable</span>
                      <span>{inr(entry.finalTotal)}</span>
                    </div>
                    {entry.payments.length > 0 && (
                      <div className="ih-payment-list">
                        {entry.payments.map((p, idx) => (
                          <div className="ih-payment-row" key={idx}>
                            <span className="ih-payment-left">
                              <span className={`ih-payment-type ih-payment-type-${(p.paymentType || '').toLowerCase()}`}>
                                {p.paymentType || '-'}
                              </span>
                              {p.paidAt && <span className="ih-payment-date">{dayjs(p.paidAt).format('DD MMM, hh:mm A')}</span>}
                              {p.note && (
                                <Tooltip title={p.note}>
                                  <EyeOutlined className="ih-remark-icon" />
                                </Tooltip>
                              )}
                            </span>
                            <span className="ih-payment-amount">{inr(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {entry.paidAmount > 0 && (
                      <div className="ih-row">
                        <span>Paid</span>
                        <span>{inr(entry.paidAmount)}</span>
                      </div>
                    )}
                    {skey !== 'paid' && (
                      <div className="ih-row">
                        <span>Due</span>
                        <span>{inr(entry.dueAmount)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pay modal ── */}
      <Modal
        title="Record Payment"
        open={!!payEntry}
        onCancel={closePayModal}
        onOk={handlePaySubmit}
        okText="Pay"
        destroyOnHidden
      >
        {payEntry && (
          <Form layout="vertical">
            <Form.Item label="Payment Mode">
              <Radio.Group
                value={paymentMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setPaymentMode(mode);
                  setPaymentAmount(mode === 'full' ? Math.max(0, payEntry.dueAmount) : null);
                }}
              >
                <Radio.Button value="full">Fully Paid</Radio.Button>
                <Radio.Button value="partial">Partial</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="Amount">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={Math.max(0, payEntry.dueAmount)}
                value={paymentAmount}
                placeholder={paymentMode === 'partial' ? 'Enter amount' : undefined}
                disabled={paymentMode === 'full'}
                onChange={(v) => setPaymentAmount(v === null ? null : Number(v) || 0)}
              />
            </Form.Item>
            <Form.Item label="Note">
              <Input.TextArea
                rows={2}
                placeholder="e.g. 1st installment"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default EmployeeIncentiveHistory;
